import React from "react";
import ReactDOM from "react-dom";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import "../styles/styles.css";

const SignupForm = () => {
  const requiredAsterisk = () => {
    return (
      <span className="text-red-700">*</span>
    );
  };

  const TextLine = ({ label, showRequiredAsterisk, ...props }) => {
    const [field, meta] = useField(props);
    return (
      <div>
        <div className="mb-4">
          <label
            className="block text-sm font-bold mb-2"
            htmlFor="{props.id || props.name}"
          >
            {label} {showRequiredAsterisk ? requiredAsterisk() : ""}
          </label>
          <input
            {...field}
            {...props}
            className="shadow appearance-none border rounded w-full py-2 px-3 mb-3 leading-tight focus:outline-none focus:shadow-outline text-black"
          />
          {meta.touched && meta.error ? (
            <div>
              <p className="text-red-700 text-xs italic">{meta.error}</p>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const TextArea = ({ label, showRequiredAsterisk, ...props }) => {
    const [field, meta] = useField(props);
    return (
      <div>
        <div className="mb-4">
          <label
            className="block text-sm font-bold mb-2"
            htmlFor="{props.id || props.name}"
          >
            {label} {showRequiredAsterisk ? requiredAsterisk() : ""}
          </label>
          {props.hasOwnProperty("description") ?
            <p className="text-sm mb-2">
              {props.description}
            </p> : ''
          }
          <textarea
            {...field}
            {...props}
            className="shadow appearance-none border rounded w-full py-2 px-3 mb-3 mt-8 leading-tight focus:outline-none focus:shadow-outline text-black"
          />
          {meta.touched && meta.error ? (
            <div>
              <p className="text-red-700 text-xs italic">{meta.error}</p>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const CheckBox = ({ children, ...props }) => {
    const [field] = useField({ ...props, type: "checkbox" });
    return (
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          {...field}
          {...props}
        />
        <label
          htmlFor="{props.id || props.name}"
          className="ml-2 text-sm font-medium"
        >
          {children}
        </label>
      </div>
    );
  };

  const Radio = ({ children, ...props }) => {
    const [field] = useField({ ...props, type: "radio" });
    return (
      <div className="flex items-center mb-4">
        <input
          type="radio"
          {...field}
          {...props}
        />
        <label
          htmlFor="{props.id || props.name}"
          className="ml-2 text-sm font-medium"
        >
          {children}
        </label>
      </div>
    );
  };

  return (
    <div className="px-6 pt-10 pb-8 sm:mx-auto sm:max-w-xl sm:px-10">
      <Formik
        initialValues={{
          attendee_name: "",
          attendee_email: "",
          attendee_jobTitle: "",
          attendee_company: "",
          attendee_nationality: "",
          attendee_country: "",

          billing_customer_type: "",
          billing_email: "",
          billing_name: "",
          billing_address1: "",
          billing_address2: "",
          billing_city: "",
          billing_zipcode: "",
          billing_country: "",
          billing_reference: "",
          billing_taxid: "",

          payment: "",

          corellium: false,
          physical_jelbrek: false,
          physical_borrow: false,

          background: "",
        }}
        validationSchema={Yup.object({
          attendee_name: Yup.string().required("Required"),
          attendee_jobTitle: Yup.string(),
          attendee_company: Yup.string(),
          attendee_email: Yup.string()
            .email("Invalid email address")
            .required("We need an email address to send you more details"),
          attendee_nationality: Yup.string().required(
            "Please enter the attendee's nationality"
          ),
          attendee_country: Yup.string()
            .required()
            .required("Please enter a country of residence"),

          /*billing_customer_type: Yup.string()
            .oneOf(["b2b", "b2c"])
            .required(
              "Please indicate whether you are ordering as an individual or a company/institution"
            ),*/
          billing_name: Yup.string().required("Required"),
          billing_email: Yup.string()
            .email("Invalid email address"),
          billing_address1: Yup.string().required("Required"),
          billing_address2: Yup.string(),
          billing_city: Yup.string().required("Required"),
          billing_zipcode: Yup.string(),
          billing_country: Yup.string().required("Required"),
          billing_reference: Yup.string(),
          billing_taxid: Yup.string().required("Please enter your tax ID if you have one."),

          payment: Yup.string()
            .oneOf(["sepa_transfer", "stripe"])
            .required("Please choose a payment method"),

          corellium: Yup.boolean().when("physical_jelbrek", {
            is: false,
            then: (schema) => schema.when("physical_borrow", {
              is: false,
              then: (schema) => schema
                .oneOf([true], "At least one research device must be provided")
                .required("At least one research device must be provided"),
            }),
          }),
          physical_jelbrek: Yup.boolean(),
          physical_borrow: Yup.boolean(),

          background: Yup.string(),
        })}
        onSubmit={(values, { setSubmitting }) => {
            fetch('https://api.reversing.training/api/signup/obtsv8', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(values)
            })
            .then(response => {
              setSubmitting(false);
              if (response.status == 202) {
                window.location.href="/registration/success"
              } else {
                window.location.href="/registration/failure"
              }
            });
        }}
      >
        {(props) => (
          <Form>
            <div className="border-b border-gray-100/10 pb-4 mb-4">
              <h2>Attendee Information</h2>
              <TextLine label="Name" name="attendee_name" type="text" showRequiredAsterisk="true" />
              <TextLine label="Email" name="attendee_email" type="email" showRequiredAsterisk="true" />
              <TextLine
                label="Nationality"
                name="attendee_nationality"
                type="text"
                showRequiredAsterisk="true"
              />
              <TextLine
                label="Country of Residence"
                name="attendee_country"
                type="text"
                showRequiredAsterisk="true"
              />
            </div>
            <div className="border-b border-gray-100/10 pb-4 mb-4">
              <h2>Billing Information</h2>
              <TextLine label="Company Name" name="billing_name" type="text" showRequiredAsterisk="true" />
              {/*
               <div className="mb-4">
                <div role="group">
                  <Radio name="billing_customer_type" value="b2b">
                    Business / Institution
                  </Radio>
                  <Radio name="billing_customer_type" value="b2c">
                    Individual
                  </Radio>
                </div>
                {props.touched.billing_customer_type &&
                props.errors.billing_customer_type ? (
                  <div>
                    <p className="text-red-700 text-xs italic">
                      {props.errors.billing_customer_type}
                    </p>
                  </div>
                ) : null}
              </div>
*/}
              <TextLine
                label="Email (for invoice)"
                name="billing_email"
                type="email"
              />
              <TextLine label="Address" name="billing_address1" type="text" showRequiredAsterisk="true" />
              <TextLine
                label="Address (continued)"
                name="billing_address2"
                type="text"
              />
              <TextLine label="Zip Code" name="billing_zipcode" type="text" />
              <TextLine label="City" name="billing_city" type="text" showRequiredAsterisk="true" />
              <TextLine label="Country" name="billing_country" type="text" showRequiredAsterisk="true" />
              <TextLine
                label="Tax ID (if applicable)"
                name="billing_taxid"
                type="text"
                showRequiredAsterisk="true" 
              />

              <div className="mb-4">
                <div className="block text-sm font-bold mb-2">
                  Preferred Payment Method {requiredAsterisk()}
                </div>
                <div role="group">
                  <Radio name="payment" value="sepa_transfer">
                    SEPA Transfer
                  </Radio>
                  <Radio name="payment" value="stripe">
                    Credit Card<br />
                  </Radio>
                </div>
                <p className="text-sm">In case of refunds, transaction and processing fees cannot be included.</p>
                {props.touched.payment && props.errors.payment ? (
                  <div>
                    <p className="text-red-700 text-xs italic">
                      {props.errors.payment}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="border-b border-gray-100/10 pb-4 mb-4">
              <div className="mb-4">
                <div className="block text-sm font-bold mb-2">
                  Setup {requiredAsterisk()}
                </div>
                <p className="text-sm">
                  Attendees need to provide their own Corellium account or
                  physical iPhone, iOS 14 or higher.
                </p>
                <div role="group" className="mt-8">
                  <CheckBox name="corellium">
                    The attendee has access to a Corellium account
                  </CheckBox>
                  <CheckBox name="physical_jelbrek">
                    The attendee has access to a jailbroken physical iPhone
                  </CheckBox>
                  <CheckBox name="physical_borrow">
                    The attendee would like to borrow an iPhone for the training
                  </CheckBox>
                </div>
                {(props.touched.corellium ||
                  props.touched.physical_borrow ||
                  props.touched.physical_jelbrek) &&
                  !(
                    props.values.corellium ||
                    props.values.physical_borrow ||
                    props.values.physical_jelbrek
                  ) ? (
                  <div>
                    <p className="text-red-700 text-xs italic">
                      Either a Correlium account or a physical iPhone is
                      required for the training.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <TextArea
                label="Background"
                name="background"
                description="Please tell us a bit about your background, your level of prior experience and your expectations for this training. This will help us better prepare and cater to individual requests and wishes."
                rows="6"
              />
            </div>
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              type="submit"
            >
              Submit
            </button>
            {/* <pre>{JSON.stringify(props, null, 2)}</pre> */}
          </Form>
        )}
      </Formik>
    </div>
  );
};


const rootElement = document.getElementById("signup-form");
ReactDOM.render(<SignupForm />, rootElement);